/**
 * Shared location-resolution utilities for the asset tracking tabs.
 * Used by: asset-tracking-map, asset-tracking-locations, sidebar.
 */

import { getToken } from '_services/user-service';

export const GPS_THRESHOLD_M = 400;

// Geo
export function haversineMetres(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const toRad = d => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Centroid of a polygon [[lat,lng], ...] or null
function polygonCentroid(polygon) {
    if (!Array.isArray(polygon) || !polygon.length) return null;
    const sum = polygon.reduce((acc, p) => ({ lat: acc.lat + p[0], lon: acc.lon + p[1] }), { lat: 0, lon: 0 });
    return { lat: sum.lat / polygon.length, lon: sum.lon / polygon.length };
}

// Circular geofence → {lat,lon} or null
function circularCoords(gf) {
    const c = gf?.circular;
    if (!c?.lat || !c?.long) return null;
    const lat = parseFloat(c.lat);
    const lon = parseFloat(c.long);
    return isNaN(lat) || isNaN(lon) ? null : { lat, lon };
}

// Best coords from geofenceDetails: circular first, polygon fallback
function geofenceCoords(loc) {
    return circularCoords(loc?.geofenceDetails) ?? polygonCentroid(loc?.geofenceDetails?.polygon);
}

// Build centroid map: locationTitle → { lat, lon }
export function buildCentroids(locations) {
    const centroids = {};
    for (const loc of (locations || [])) {
        if (!loc.title) continue;
        const coords = geofenceCoords(loc);
        if (coords) centroids[loc.title] = coords;
    }
    return centroids;
}

// Find nearest centroid → { title, distance } | null
export function nearestCentroid(lat, lon, centroids) {
    let best = null;
    let bestDist = Infinity;
    for (const [title, c] of Object.entries(centroids)) {
        const d = haversineMetres(lat, lon, c.lat, c.lon);
        if (d < bestDist) { bestDist = d; best = title; }
    }
    return best ? { title: best, distance: Math.round(bestDist) } : null;
}

// BLE helpers 

// Find the location doc that matches the highest-RSSI BLE item
function findNearestBeacon(bleItems, locations) {
    if (!bleItems?.length || !locations?.length) return null;
    const nearest = bleItems.reduce(
        (prev, curr) => (curr.rssi > prev.rssi ? curr : prev),
        { rssi: -1000 }
    );
    if (!nearest?.mac) return null;
    const mac = nearest.mac.toLowerCase();
    return locations.find(loc =>
        (loc.beaconDetails ?? []).some(b => b?.iBeaconMacAddress?.toLowerCase() === mac)
    ) || null;
}

// BLE → location title
export function matchBle(bleItems, locations) {
    return findNearestBeacon(bleItems, locations)?.title || null;
}

// BLE → { latitude, longitude } for map placement (circular → polygon centroid fallback)
export function bleToCoords(bleItems, locations) {
    const loc = findNearestBeacon(bleItems, locations);
    const coords = geofenceCoords(loc);
    if (!coords) return null;
    return { latitude: coords.lat, longitude: coords.lon };
}

// Prefer locationId from the fingerprint API (the surveyed room) over geometric centroid lookup.
// Returns { title, outOfRange, distance } or null.
function resolveWifiToTitle(wifiResult, locations, centroids) {
    if (!wifiResult?.located) return null;
    if (wifiResult.locationId) {
        const title = (locations ?? []).find(l => l._id === wifiResult.locationId)?.title;
        if (title) return { title, outOfRange: false, distance: null };
    }
    if (wifiResult.freeTextLabel) return { title: wifiResult.freeTextLabel, outOfRange: false, distance: null };
    const hit = nearestCentroid(wifiResult.lat, wifiResult.lng, centroids);
    if (!hit) return null;
    return { title: hit.title, outOfRange: hit.distance > GPS_THRESHOLD_M, distance: hit.distance };
}

// Location label
/**
 * Resolve a device's current position to a human-readable location label.
 *
 * Returns { label: string, source: 'ble' | 'gps' | 'wifi' | 'last-known' } or null.
 *
 * Logic (mirrors the Locations tab):
 *   1. Try the current location_fixed payload (BLE or GPS).
 *   2. Fall back to lastValidPosition for all other payload types
 *      (location_failure, event, heartbeat, shutdown) — labelled "last known".
 */
export function resolveLocationLabel(device, locations, centroids, wifiLocations = {}) {
    const latest = device?.latest;
    const lvp    = device?.lastValidPosition;
    const wifiResult = wifiLocations[device.id];

    // 1. Current location_fixed
    if (latest?.payloadType === 'location_fixed' || latest?.payloadType === 'location_fixed_gps_limit') {
        if (latest.positioningType === 'bluetooth') {
            const title = matchBle(latest.bleItems, locations);
            if (title) return { label: title, source: 'ble' };
        } else if (latest.positioningType === 'gps' && latest.gpsData?.latitude) {
            const hit = nearestCentroid(latest.gpsData.latitude, latest.gpsData.longitude, centroids);
            if (hit) {
                if (hit.distance <= GPS_THRESHOLD_M) return { label: `${hit.title} · ${hit.distance}m`, source: 'gps' };
                return { label: `Out of range (nearest: ${hit.title} · ${hit.distance}m)`, source: 'gps' };
            }
        } else if (latest.positioningType === 'wifi' && wifiResult?.located) {
            const wr = resolveWifiToTitle(wifiResult, locations, centroids);
            if (wr) {
                if (!wr.outOfRange) return { label: wr.distance != null ? `${wr.title} · ${wr.distance}m` : wr.title, source: 'wifi' };
                return { label: `Out of range (nearest: ${wr.title}${wr.distance != null ? ` · ${wr.distance}m` : ''})`, source: 'wifi' };
            }
        }
    }

    // 2. Fallback: wifi result if more recent than LVP, then LVP itself
    const lvpAt = lvp?.at?.toMillis?.() ?? 0;
    if (wifiResult?.located && (wifiResult.resolvedAt ?? 0) > lvpAt) {
        const wr = resolveWifiToTitle(wifiResult, locations, centroids);
        if (wr && !wr.outOfRange) return { label: wr.distance != null ? `${wr.title} · ${wr.distance}m` : wr.title, source: 'last-known' };
    }
    if (lvp) {
        if (lvp.positioningType === 'bluetooth') {
            const title = matchBle(lvp.bleItems, locations);
            if (title) return { label: title, source: 'last-known' };
        } else if (lvp.positioningType === 'gps' && lvp.gpsData?.latitude) {
            const hit = nearestCentroid(lvp.gpsData.latitude, lvp.gpsData.longitude, centroids);
            if (hit && hit.distance <= GPS_THRESHOLD_M) return { label: `${hit.title} · ${hit.distance}m`, source: 'last-known' };
        } else if (lvp.positioningType === 'wifi' && wifiResult?.located) {
            const wr = resolveWifiToTitle(wifiResult, locations, centroids);
            if (wr && !wr.outOfRange) return { label: wr.distance != null ? `${wr.title} · ${wr.distance}m` : wr.title, source: 'last-known' };
        }
    }

    return null;
}

// Gateway helpers
export function resolveGatewayName(gatewayId, gateways) {
    if (!gatewayId) return null;
    return (gateways ?? []).find(g => g.gatewayId === gatewayId)?.name ?? gatewayId;
}

// Shared display constants
export const OUT_OF_RANGE_KEY = '__out_of_range__';
export const UNRESOLVED_KEY   = '__unresolved__';
export const FIVE_MINUTES_MS  = 5 * 60 * 1000;

export const POS_BADGE = {
    bluetooth: { bg: '#dbeafe', color: '#1e4fa0', label: 'BLE'  },
    gps:       { bg: '#dcfce7', color: '#15623a', label: 'GPS'  },
    wifi:      { bg: '#ede9fe', color: '#5b21b6', label: 'WiFi' },
};

export const SOURCE_BADGE = {
    ble:          { bg: '#dbeafe', color: '#1e4fa0', label: 'BLE'        },
    gps:          { bg: '#dcfce7', color: '#15623a', label: 'GPS'        },
    wifi:         { bg: '#ede9fe', color: '#5b21b6', label: 'WiFi'       },
    'last-known': { bg: '#fef3c7', color: '#92400e', label: 'Last known' },
};

export const LEGEND_ITEMS = [
    { color: '#007bff', label: 'Fixed'   },
    { color: '#36ed6d', label: 'Moving'  },
    { color: '#7eb8e0', label: 'Stopped' },
    { color: '#f0a030', label: 'No Fix'  },
    { color: '#808080', label: 'Offline' },
];

// Lower index = higher visual priority (shown when multiple colours in a cluster)
export const COLOR_PRIORITY = ['#36ed6d', '#f0a030', '#007bff', '#7eb8e0', '#808080'];

export function pickDominantColor(colors) {
    let best = colors[0], bestIdx = COLOR_PRIORITY.indexOf(best);
    if (bestIdx === -1) bestIdx = 999;
    for (const c of colors) {
        const idx = COLOR_PRIORITY.indexOf(c);
        const eff = idx === -1 ? 999 : idx;
        if (eff < bestIdx) { best = c; bestIdx = eff; }
    }
    return best;
}

export function isDeviceStale(device, now) {
    const lastMotion = device?.meta?.deviceLastMotionAt;
    if (!lastMotion) return false;
    return (now - lastMotion.toDate().getTime()) > FIVE_MINUTES_MS;
}

export function deviceMarkerColor(device) {
    const p = device?.latest?.payloadType;
    const m = device?.latest?.stillInMotion;
    const e = device?.latest?.eventType;
    if (p === 'location_fixed' || p === 'location_fixed_gps_limit') return m ? '#36ed6d' : '#007bff';
    if (p === 'event') return e === 'Start of movement' ? '#36ed6d' : '#7eb8e0';
    if (p === 'location_failure') return '#f0a030';
    if (p === 'shutdown') return '#808080';
    return '#007bff';
}

export function deviceStateLabel(device) {
    const p = device?.latest?.payloadType;
    const m = device?.latest?.stillInMotion;
    const e = device?.latest?.eventType;
    if (p === 'location_fixed' || p === 'location_fixed_gps_limit') return m ? 'Moving' : 'Stationary';
    if (p === 'event') return e === 'Start of movement' ? 'Moving' : 'Stopped';
    if (p === 'location_failure') return 'No fix';
    if (p === 'heartbeat') return 'Device OK';
    if (p === 'shutdown') return 'Offline';
    return 'Unknown';
}

/**
 * Central device resolver - single source of truth for both the map and the
 * locations list. Returns one object per device with all data needed by both
 * panels. lat/lon may be null for devices that cannot be placed on the map.
 */
export function resolveDevice(device, locations, centroids, wifiLocations, gateways, now) {
    const latest      = device?.latest;
    const lvp         = device?.lastValidPosition;
    const wifiResult  = (wifiLocations || {})[device?.id];
    const payloadType = latest?.payloadType;
    const latestPT    = latest?.positioningType;
    const stillInMotion = latest?.stillInMotion;
    const eventType   = latest?.eventType;

    let lat = null, lon = null;
    let markerColor  = deviceMarkerColor(device);
    let locationKey  = UNRESOLVED_KEY;
    let locationTitle = null;
    let source       = 'unresolved';
    let lvpType      = null;
    let distance     = null;
    let nearestTitle = null;
    let resolvedPT   = latestPT ?? null;

    // Helper: apply lastValidPosition to fill missing coords / location
    function applyLvp() {
        const lvpAt = lvp?.at?.toMillis?.() ?? 0;

        // WiFi resolved more recently than LVP → treat it as the last valid fix
        if (wifiResult?.located && (wifiResult.resolvedAt ?? 0) > lvpAt) {
            lvpType = 'wifi'; resolvedPT = 'wifi';
            if (lat == null) { lat = wifiResult.lat; lon = wifiResult.lng; }
            if (locationKey === UNRESOLVED_KEY) {
                const wr = resolveWifiToTitle(wifiResult, locations, centroids);
                if (wr) {
                    if (!wr.outOfRange) { locationKey = wr.title; locationTitle = wr.title; }
                    else { locationKey = OUT_OF_RANGE_KEY; nearestTitle = wr.title; }
                    if (wr.distance != null) distance = wr.distance;
                    source = 'last-known';
                }
            }
            return;
        }

        if (!lvp) return;
        lvpType   = lvp.positioningType;
        resolvedPT = lvpType;
        if (lvpType === 'bluetooth') {
            if (lat == null) {
                const c = bleToCoords(lvp.bleItems, locations);
                if (c) { lat = c.latitude; lon = c.longitude; }
            }
            if (locationKey === UNRESOLVED_KEY) {
                const title = matchBle(lvp.bleItems, locations);
                if (title) { locationKey = title; locationTitle = title; source = 'last-known'; }
            }
        } else if (lvpType === 'gps' && lvp.gpsData?.latitude != null) {
            if (lat == null) { lat = lvp.gpsData.latitude; lon = lvp.gpsData.longitude; }
            if (locationKey === UNRESOLVED_KEY) {
                const hit = nearestCentroid(lvp.gpsData.latitude, lvp.gpsData.longitude, centroids);
                if (hit) {
                    distance = hit.distance;
                    if (hit.distance <= GPS_THRESHOLD_M) {
                        locationKey = hit.title; locationTitle = hit.title;
                    } else {
                        locationKey = OUT_OF_RANGE_KEY; nearestTitle = hit.title;
                    }
                    source = 'last-known';
                }
            }
        } else if (lvpType === 'wifi' && wifiResult?.located) {
            if (lat == null) { lat = wifiResult.lat; lon = wifiResult.lng; }
            if (locationKey === UNRESOLVED_KEY) {
                const wr = resolveWifiToTitle(wifiResult, locations, centroids);
                if (wr) {
                    if (!wr.outOfRange) { locationKey = wr.title; locationTitle = wr.title; }
                    else { locationKey = OUT_OF_RANGE_KEY; nearestTitle = wr.title; }
                    if (wr.distance != null) distance = wr.distance;
                    source = 'last-known';
                }
            }
        }
    }

    // Primary resolution for location_fixed / location_fixed_gps_limit
    if (payloadType === 'location_fixed' || payloadType === 'location_fixed_gps_limit') {
        if (latestPT === 'bluetooth') {
            const c = bleToCoords(latest?.bleItems, locations);
            if (c) { lat = c.latitude; lon = c.longitude; }
            const title = matchBle(latest?.bleItems, locations);
            if (title) { locationKey = title; locationTitle = title; source = 'ble'; }
        } else if (latestPT === 'gps' && latest?.gpsData?.latitude != null) {
            lat = latest.gpsData.latitude;
            lon = latest.gpsData.longitude;
            const hit = nearestCentroid(lat, lon, centroids);
            if (hit) {
                distance = hit.distance;
                if (hit.distance <= GPS_THRESHOLD_M) {
                    locationKey = hit.title; locationTitle = hit.title;
                } else {
                    locationKey = OUT_OF_RANGE_KEY; nearestTitle = hit.title;
                }
            }
            source = 'gps';
        } else if (latestPT === 'wifi' && wifiResult?.located) {
            lat = wifiResult.lat;
            lon = wifiResult.lng;
            const wr = resolveWifiToTitle(wifiResult, locations, centroids);
            if (wr) {
                if (!wr.outOfRange) { locationKey = wr.title; locationTitle = wr.title; }
                else { locationKey = OUT_OF_RANGE_KEY; nearestTitle = wr.title; }
                if (wr.distance != null) distance = wr.distance;
            }
            source = 'wifi';
        }
        // If primary still has no coords, fall back to LVP
        if (lat == null) applyLvp();
    } else if (payloadType === 'shutdown' && latestPT === 'bluetooth') {
        const c = bleToCoords(latest?.bleItems, locations);
        if (c) { lat = c.latitude; lon = c.longitude; }
        const title = matchBle(latest?.bleItems, locations);
        if (title) { locationKey = title; locationTitle = title; source = 'ble'; }
        if (lat == null) applyLvp();
    } else {
        // heartbeat, location_failure, event, unknown → use LVP
        applyLvp();
    }

    // Build info object (same keys as the existing map popup)
    const gwId   = device?.meta?.gatewayId;
    const gwName = gateways?.find?.(g => g.gatewayId === gwId)?.name ?? gwId ?? 'N/A';
    const lastActive = device?.meta?.deviceLastMotionAt;

    let locDisplayLabel;
    if (source === 'last-known' && locationTitle) {
        locDisplayLabel = `${locationTitle} (last known)`;
    } else if (locationKey === OUT_OF_RANGE_KEY) {
        locDisplayLabel = nearestTitle ? `Out of range · ${nearestTitle}` : 'Out of range';
    } else {
        locDisplayLabel = locationTitle ?? 'Unknown';
    }

    const floorLabel = (source === 'wifi' || source === 'last-known') ? (wifiResult?.floorLabel ?? null) : null;

    const info = {
        'Location':        locDisplayLabel,
        ...(floorLabel ? { 'Floor': floorLabel } : {}),
        'Last Movement':   lastActive ? lastActive.toDate().toLocaleString() : 'N/A',
        'Battery Low':     device?.meta?.batteryLow ? 'Yes' : 'No',
        'Temperature':     device?.meta?.deviceTemp ? `${device.meta.deviceTemp} °C` : 'N/A',
        'Operation Mode':  latest?.operationMode ?? 'N/A',
        'Nearest Gateway': gwName,
    };
    if (payloadType === 'location_failure') {
        info['Failure Reason'] = latest?.positioningFailureReason ?? 'N/A';
    }
    if (payloadType === 'event' || payloadType === 'heartbeat') {
        info['In Motion'] = stillInMotion ? 'Yes' : 'No';
    }
    if (payloadType === 'shutdown') {
        info['In Motion'] = 'No';
    }

    return {
        device,
        lat,
        lon,
        markerColor,
        info,
        stale:          isDeviceStale(device, now),
        positioningType: resolvedPT,
        // Locations list fields
        locationKey,
        locationTitle,
        floorLabel,
        source,
        lvpType,
        distance,
        nearestTitle,
    };
}

// WiFi event resolution

/**
 * Batch-resolve WiFi location_fixed events via the fingerprint locate API.
 * Returns Map<eventIndex, {lat, lng}> for resolved events only.
 * Deduplicates by MAC fingerprint so each unique scan set calls the API once.
 */
export async function resolveWifiEventsToCoords(events, iata) {
    if (!iata) return new Map();

    // TODO: Dub has no active email hence using ork email and hardcoding iata here to dub
    if(!['DUB'].includes(iata)){
        iata = 'DUB';
    }
    const wifiEvents = events
        .map((e, i) => ({ i, e }))
        .filter(({ e }) =>
            (e.type === 'location_fixed' || e.type === 'location_fixed_gps_limit') &&
            e.decoded?.positioningType === 'WiFi positioning success' &&
            e.decoded?.wifi?.length
        );

    if (!wifiEvents.length) return new Map();

    const token = await getToken();
    const fpCache = new Map();
    const result = new Map();

    for (const { i, e } of wifiEvents) {
        const scans = e.decoded.wifi;
        const fp = scans.map(s => s.mac).sort().join(',');
        if (!fpCache.has(fp)) {
            try {
                const res = await fetch(`/api/v1/mobileusers/wifi-survey/locate/${iata}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Firebase-UID': token },
                    body: JSON.stringify({ scans }),
                });
                const data = res.ok ? await res.json() : null;
                fpCache.set(fp, data?.located ? { lat: data.lat, lng: data.lng, locationId: data.locationId ?? null, freeTextLabel: data.freeTextLabel ?? null, floorLabel: data.floorLabel ?? null } : null);
            } catch {
                fpCache.set(fp, null);
            }
        }
        const coords = fpCache.get(fp);
        if (coords) result.set(i, coords);
    }
    return result;
}
